import {
  Box,
  Separator,
  Flex,
  Heading,
  Spinner,
  Table,
  TableCaption,
  Text,
} from '@chakra-ui/react';
import colors from 'src/theme/colors';
import { useFormContext } from 'react-hook-form';
import useFeatureFlag from 'src/components/feature-flags/useFeatureFlag';
import Fade from 'src/components/ui/Fade';
import useFetchCost from '../hooks/useFetchCost';
import useFetchTaxPercentage from '../hooks/useFetchTaxPercentage';
import { Discount, Plan } from '../types';
import getTrialExpiryDate from '../helpers/getTrialExpiryDate';

interface calculateCostBreakdownArgs {
  cost: number;
  hostingCost: number;
  currency: string;
  currencySymbol: string;
  isLoading: boolean;
  vatPercentage: number;
  isEuropeanUnion: boolean;
  discount: Discount;
}

function calculateCostBreakdown({
  cost,
  currency,
  currencySymbol,
  isLoading,
  vatPercentage,
  isEuropeanUnion,
  discount,
  hostingCost,
}: calculateCostBreakdownArgs) {
  const currencyCode = currency === 'cad' || currency === 'aud' ? currency.toUpperCase() : '';
  const includedVat = (cost + hostingCost) * (vatPercentage / 100);
  let totalCost = (cost + hostingCost) * ((100 - discount.percentOff) / 100);

  if (discount.amountOff) {
    totalCost = cost + hostingCost - discount.amountOff / 100;
  }

  return {
    includedVat: `${currencySymbol}${includedVat.toFixed(2)}`,
    subtotal: `${currencySymbol}${(totalCost - includedVat).toFixed(2)}`,
    shouldDisplaySubtotal: (vatPercentage || vatPercentage === 0) && isEuropeanUnion && !isLoading,
    costBeforeDiscount: isLoading ? null : `${currencySymbol}${cost + hostingCost} ${currencyCode}`,
    hostingWithoutVat: isEuropeanUnion ? ((100 - vatPercentage) / 100) * hostingCost : hostingCost,
    planWithoutVat: isEuropeanUnion ? ((100 - vatPercentage) / 100) * cost : cost,
    totalCost: isLoading ? null : `${currencySymbol}${totalCost.toFixed(2)} ${currencyCode}`,
  };
}

export default function OrderSummary({ withTrial = true }: { withTrial?: boolean }) {
  const {
    watch,
    formState: { errors },
  } = useFormContext();

  const plan = watch('plan');
  const hostingPlan = watch('hostingPlan');
  const countryCode = watch('country');
  const vatNumber = watch('vatNumber');
  const discount = watch('discount');
  const promoCode = watch('promoCode');
  const isEuropeanUnion = watch('isEuropeanUnion');
  const hasLongerTrialFlag = useFeatureFlag('14-day-trial');
  const { cost, currencySymbol, currency, isLoading } = useFetchCost({
    plan,
    countryCode,
    isEuropeanUnion,
  });

  const { cost: hostingCost } = useFetchCost({
    plan: `${plan}_hosting_${hostingPlan || '1k'}` as Plan,
    countryCode,
    isEuropeanUnion,
  });
  const isVatNumberValid = !errors.vatNumber;
  const vatPercentage = useFetchTaxPercentage({
    countryCode,
    vatNumber: isVatNumberValid ? vatNumber : '',
  });

  const {
    includedVat,
    subtotal,
    shouldDisplaySubtotal,
    costBeforeDiscount,
    totalCost,
    hostingWithoutVat,
    planWithoutVat,
  } = calculateCostBreakdown({
    cost,
    currencySymbol,
    currency,
    isLoading,
    vatPercentage,
    isEuropeanUnion,
    discount,
    hostingCost,
  });

  return (
    <>
      {isLoading && (
        <Box textAlign="center">
          <Spinner />
        </Box>
      )}
      <Fade in={!isLoading}>
        <Flex flexDir="column">
          <Separator display={['block', null, 'none']} />
          <Flex alignItems="center" justifyContent="space-between">
            <Heading size="h5">Your {plan} payment breakdown:</Heading>
          </Flex>
          <Table.Root visual="costBreakdown" mt="20px">
            <Table.Body>
              <Table.Row>
                <Table.Cell>Your Alitu Plan</Table.Cell>
                <Table.Cell textAlign="right">
                  + {currencySymbol}
                  {planWithoutVat.toFixed(2)}
                </Table.Cell>
              </Table.Row>
              {hostingWithoutVat > 0 && (
                <Table.Row>
                  <Table.Cell>Hosting Package ({hostingPlan} downloads per month)</Table.Cell>
                  <Table.Cell textAlign="right">
                    + {currencySymbol} {hostingWithoutVat.toFixed(2)}
                  </Table.Cell>
                </Table.Row>
              )}
              {shouldDisplaySubtotal && (
                <>
                  <Table.Row borderTop={`1px solid ${colors.podcastShade200}`}>
                    <Table.Cell>Subtotal</Table.Cell>
                    <Table.Cell textAlign="right">= {subtotal}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>VAT ({Math.round(vatPercentage)}%)</Table.Cell>
                    <Table.Cell textAlign="right">{`+ ${includedVat}`}</Table.Cell>
                  </Table.Row>
                </>
              )}
              {(discount.percentOff > 0 || discount.amountOff > 0) && (
                <Table.Row
                  borderTop={
                    !shouldDisplaySubtotal ? `1px solid ${colors.podcastShade200}` : 'none'
                  }
                >
                  <Table.Cell colSpan={2}>
                    Discount - &nbsp;
                    {promoCode.toUpperCase()}
                    <br /> {discount.terms}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.Cell>
                  <Text size="p1" visual="bold">
                    Total
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="right" data-cy="totalCost">
                  <Text size="p1" visual="bold">
                    {' '}
                    {totalCost}
                  </Text>
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
            <TableCaption>
              {discount.percentOff || discount.amountOff ? (
                <div>
                  {discount.duration === 'once'
                    ? `${costBeforeDiscount} ${plan} after that`
                    : `Was ${costBeforeDiscount}`}
                </div>
              ) : null}
              {withTrial && (
                <div>
                  Billed {plan} after your trial ends on{' '}
                  {getTrialExpiryDate(discount, hasLongerTrialFlag)}
                </div>
              )}
            </TableCaption>
          </Table.Root>
        </Flex>
      </Fade>
    </>
  );
}
